import { IEKGEvent } from '../store/ekgImport.types'
import { IItem } from '../../timeline/store/timeline.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IAirwayState } from '../../procedures/airway/store/airway.types'
import { IOptions } from '../../../common/common.types'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { handleNemsisCodeError, getUpdatedProcedureStateWithNemsisCode } from '../../../utilities/nemsisCode.util'

interface CreateAirwayProcedureProps {
  procedureEvent: IEKGEvent
  airwayOptions: IOptions
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface AirwayProcedureObject {
  timelineItem: IItem
  airwayState: IAirwayState
}

const createAirwayProcedure = (props: CreateAirwayProcedureProps): AirwayProcedureObject => {
  const { procedureEvent, airwayOptions, ptaCutoff, nemsisCodeOptions } = props

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const airwayOption = airwayOptions.find((option) => (option.id ?? '').toString() === procedureEvent.procedureAction)
  const itemComments = procedureEvent.procedureComments || ''
  const itemSubHeader = `By , Action: ${
    airwayOption?.name || ''
  }, Flow: , FiO2: , Humidity: , Air Temperature: ° , Attempts: 1, Success: Yes, Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.AIRWAY_PROCEDURE,
    airwayOption?.name || '',
    { filter1: '', filter2: '', filter3: '' },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    clientCorrelationId,
    itemType: 'Procedures',
    event: 'Airway',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_AIRWAY_PROCEDURE_ITEM,
    defaultHref: TabConstants.AIRWAY_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  let airwayState: IAirwayState = {
    key: clientEventId,
    action: airwayOption ?? null,
    flow: '',
    fiO2: '',
    via: null,
    humidity: '',
    oralPharyngealAirway: false,
    airTemperature: '',
    airTemperatureUnit: { id: 'c', name: 'C' },
    airTemperatureBool: false,
    airwayComplications: [],
    airwayComplicationsPNNV: null,
    pressure: '',
    inspiratoryPressure: '',
    expiratoryPressure: '',
    assessedBy: null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    addToTimeline: true,
    updateInputRows: false,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    type: 'Airway',
    size: '',
    rolePerforming: null,
    rolePerformingPNNV: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
    verificationMethod: [],
    isEkgImport: 1,
    airwayDefaults: null,
    reasonsForFailure: [],
    nemsisCode: null,
  }

  try {
    airwayState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: airwayState,
      actionField: null,
      filter1Field: null,
      filter2Field: null,
      filter3Field: null,
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    airwayState = handleNemsisCodeError(error, airwayState)
  }

  const airwayProcedureObject: AirwayProcedureObject = {
    airwayState,
    timelineItem,
  }

  return airwayProcedureObject
}

export default createAirwayProcedure
